import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTrash, FaSpinner } from 'react-icons/fa';
import { FunctionComponent } from 'react';
import { deleteUser, logoutUser } from '../lib/api';

interface Props {
  setIsLoggedIn: (value: boolean) => void;
}

const DeleteProfile: React.FC<Props> = ({ setIsLoggedIn }) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('jwt'));
  const navigate = useNavigate();
  const location = useLocation();
  const FaTrashIcon = FaTrash as FunctionComponent<{ className?: string }>;
  const FaSpinnerIcon = FaSpinner as FunctionComponent<{ className?: string }>;

  // Delayed redirect for unauthenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/login', { state: { from: '/delete-profile' } });
      }, 2000); // 2-second delay
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, navigate]);

  const handleDelete = async () => {
    if (!isAuthenticated) {
      setError('You must be logged in to delete your account');
      return;
    }

    setIsLoading(true);
    setError(null);

    const token = localStorage.getItem('jwt');
    if (!token) {
      setError('Authentication token missing');
      setIsLoading(false);
      navigate('/login', { state: { from: '/delete-profile' } });
      return;
    }

    try {
      await deleteUser(token);
      logoutUser();
      setIsLoggedIn(false);
      localStorage.removeItem('userId');
      localStorage.removeItem('jwt');
      setIsModalOpen(false);
      navigate('/login', { state: { message: 'Your account has been deleted successfully.' } });
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete account');
      setIsLoading(false);
    }
  };

  const openModal = () => {
    if (!isAuthenticated) {
      setError('Please log in to proceed with account deletion');
      return;
    }
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setError(null);
  };

  return (
    <div className="container mx-auto p-6 max-w-lg min-h-screen flex flex-col justify-center">
      <h1 className="text-3xl font-bold mb-6 text-text flex items-center">
        <FaTrashIcon className="mr-2 text-error" /> Delete Account
      </h1>
      <div className="bg-white shadow-md rounded-lg p-6 flex-grow">
        <p className="text-text mb-6 text-lg">
          {isAuthenticated
            ? 'Deleting your account will permanently remove all your data, including your profile, BMI records, meal plans, and favorite recipes. This action cannot be undone.'
            : 'Please log in to delete your account. You will be redirected to the login page shortly.'}
        </p>
        {error && (
          <p className="text-error mb-4 bg-error/10 p-3 rounded-md" role="alert">
            {error}
          </p>
        )}
        {!isAuthenticated && (
          <p className="text-primary mb-4 bg-primary/10 p-3 rounded-md" role="alert">
            Redirecting to login in a few seconds...
          </p>
        )}
        <button
          onClick={openModal}
          className="w-full bg-error text-white p-3 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-error transition-colors disabled:opacity-50 flex items-center justify-center"
          disabled={isLoading || !isAuthenticated}
          aria-label="Delete Account"
        >
          {isLoading ? (
            <>
              <FaSpinnerIcon className="animate-spin mr-2 h-5 w-5" /> Deleting...
            </>
          ) : (
            'Delete My Account'
          )}
        </button>
      </div>

      {/* Confirmation Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 id="modal-title" className="text-2xl font-bold text-text mb-4">
              Confirm Account Deletion
            </h2>
            <p className="text-text mb-6">
              Are you absolutely sure you want to delete your account? This action is irreversible.
            </p>
            <div className="flex space-x-4">
              <button
                onClick={handleDelete}
                className="flex-1 bg-error text-white p-3 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-error transition-colors disabled:opacity-50 flex items-center justify-center"
                disabled={isLoading}
                aria-label="Confirm Delete"
              >
                {isLoading ? (
                  <>
                    <FaSpinnerIcon className="animate-spin mr-2 h-5 w-5" /> Deleting...
                  </>
                ) : (
                  'Yes, Delete'
                )}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 bg-gray-300 text-text p-3 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                disabled={isLoading}
                aria-label="Cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteProfile;